import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../../components/ui/table';
import { SortableTh } from '../../../components/common/SortableTh';
import { ListPager, ListSearch, useListControls } from '../../../components/common/listControls';
import type { DivisionPerformanceRow, SupplierPerformanceRow } from '../../../lib/api';

const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);

function PctCell({ value, target }: { value: number | null; target?: number }) {
  const below = target !== undefined && value !== null && value < target;
  return (
    <TableCell className={`text-right tabular-nums ${below ? 'text-red-700 dark:text-red-300 font-medium' : ''}`}>
      {pct(value)}
    </TableCell>
  );
}

interface SupplierTableProps {
  rows: SupplierPerformanceRow[];
  target: number;
  selectedId: string;
  onSelect: (id: string) => void;
}

/** Suppliers in the range, busiest first; click a row to filter the dashboard to it. */
export function SupplierPerformanceTable({ rows, target, selectedId, onSelect }: SupplierTableProps) {
  const list = useListControls(rows, {
    searchText: (r) => r.name,
    sorters: {
      name: (r) => r.name.toLowerCase(),
      lines: (r) => r.lines,
      on_time: (r) => r.on_time,
      otif: (r) => r.otif,
      required: (r) => r.required,
      below: (r) => r.months_below_target,
      streak: (r) => r.consecutive_below_target,
    },
    defaultSort: { key: 'lines', dir: 'desc' },
    tieBreak: (r) => r.name,
  });
  const th = (field: string, label: string, align: 'left' | 'right' = 'right') => (
    <SortableTh field={field} sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align={align}>
      {label}
    </SortableTh>
  );

  return (
    <Card data-testid="supplier-performance-suppliers">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Suppliers</CardTitle>
        <CardDescription>
          On-time performance per supplier for the period. Months below the {target}% target are counted, with the
          latest run of consecutive months below it. Click a supplier to see only their performance.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search suppliers…" testId="supplier-performance-suppliers-search" />
        <ListPager list={list} position="top" noun="suppliers" testIdPrefix="supplier-performance-suppliers" />
        <Table>
          <TableHeader>
            <TableRow>
              {th('name', 'Supplier', 'left')}
              {th('lines', 'Lines')}
              {th('on_time', 'On time')}
              {th('otif', 'OTIF')}
              {th('required', 'Required date')}
              {th('below', 'Months below target')}
              {th('streak', 'Consecutive below')}
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.pageRows.map((r) => (
              <TableRow key={r.id} className={`cursor-pointer ${String(r.id) === selectedId ? 'bg-muted' : ''}`}
                onClick={() => onSelect(String(r.id) === selectedId ? '' : String(r.id))}
                data-testid={`supplier-performance-supplier-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-right tabular-nums">{r.lines.toLocaleString()}</TableCell>
                <PctCell value={r.on_time} target={target} />
                <PctCell value={r.otif} />
                <PctCell value={r.required} />
                <TableCell className="text-right tabular-nums">
                  {r.months_below_target} of {r.months_reported}
                </TableCell>
                <TableCell className="text-right">
                  {r.consecutive_below_target >= 3 ? (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900">
                      {r.consecutive_below_target} months
                    </Badge>
                  ) : (
                    <span className="tabular-nums text-muted-foreground">{r.consecutive_below_target}</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {list.pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground" data-testid="supplier-performance-suppliers-empty">
                  No suppliers match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ListPager list={list} position="bottom" noun="suppliers" testIdPrefix="supplier-performance-suppliers" />
      </CardContent>
    </Card>
  );
}

interface DivisionTableProps {
  rows: DivisionPerformanceRow[];
  target: number;
  selectedId: string;
  onSelect: (id: string) => void;
}

/** Divisions in the range; click a row to filter the dashboard to it. */
export function DivisionPerformanceTable({ rows, target, selectedId, onSelect }: DivisionTableProps) {
  const list = useListControls(rows, {
    searchText: (r) => r.name,
    sorters: {
      name: (r) => r.name.toLowerCase(),
      lines: (r) => r.lines,
      suppliers: (r) => r.suppliers,
      on_time: (r) => r.on_time,
      otif: (r) => r.otif,
      required: (r) => r.required,
    },
    defaultSort: { key: 'lines', dir: 'desc' },
    tieBreak: (r) => r.name,
  });
  const th = (field: string, label: string, align: 'left' | 'right' = 'right') => (
    <SortableTh field={field} sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align={align}>
      {label}
    </SortableTh>
  );

  return (
    <Card data-testid="supplier-performance-divisions">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Divisions</CardTitle>
        <CardDescription>
          The upload files carry no division, so each delivery is credited to the division its PO site invoices to.
          Deliveries on sites with no clear division are shown as Unassigned. Click a division to filter to it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search divisions…" testId="supplier-performance-divisions-search" />
        <ListPager list={list} position="top" noun="divisions" testIdPrefix="supplier-performance-divisions" />
        <Table>
          <TableHeader>
            <TableRow>
              {th('name', 'Division', 'left')}
              {th('lines', 'Lines')}
              {th('suppliers', 'Suppliers')}
              {th('on_time', 'On time')}
              {th('otif', 'OTIF')}
              {th('required', 'Required date')}
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.pageRows.map((r) => (
              <TableRow key={String(r.id)} className={`cursor-pointer ${String(r.id) === selectedId ? 'bg-muted' : ''}`}
                onClick={() => onSelect(String(r.id) === selectedId ? '' : String(r.id))}
                data-testid={`supplier-performance-division-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-right tabular-nums">{r.lines.toLocaleString()}</TableCell>
                <TableCell className="text-right tabular-nums">{r.suppliers.toLocaleString()}</TableCell>
                <PctCell value={r.on_time} target={target} />
                <PctCell value={r.otif} />
                <PctCell value={r.required} />
              </TableRow>
            ))}
            {list.pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground" data-testid="supplier-performance-divisions-empty">
                  No divisions match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ListPager list={list} position="bottom" noun="divisions" testIdPrefix="supplier-performance-divisions" />
      </CardContent>
    </Card>
  );
}
